import React from 'react';

// Third Party Libraries
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

//Hooks
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Utils
import { MESSAGES } from '~/utils/message';
import { dateFromString, dateProtector, isValidDate } from '~/utils/date-format';
import { validURL } from '~/utils/helperFunctions';

// MUI Components
import { Box, Typography, TextField, InputAdornment, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Images
import { NoCustomFieldsCIcon } from '~/assets/images';

// Styles
import './styles.scss';

const URL_CUSTOM_FIELD_TYPES = ['url', 'image_url'];
const URL_CUSTOM_FIELD_TYPE_TITLES = ['URL', 'Image URL'];

export const getCustomFieldName = (field) =>
  Object.keys(field || {})[0];

const getCustomFieldValue = (field) => {
  const fieldName = getCustomFieldName(field);
  return fieldName ? field[fieldName] : '';
};

const getFieldType = (fieldName, allCustomFields) => {
  const matchedField = allCustomFields?.find((field) => field.value === fieldName);
  return {
    type: matchedField?.type || 'single_text',
    typeTitle: matchedField?.typeTitle || '',
  };
};

const getFieldPlaceholder = (fieldType) =>
  MESSAGES.SETTINGS.CUSTOM_FIELDS.TYPE_PLACEHOLDERS[fieldType] ||
  MESSAGES.SETTINGS.CUSTOM_FIELDS.TYPE_PLACEHOLDERS.single_text;

const parseCustomFieldDate = (value) => {
  if (!value) return null;
  const parsed = dateFromString(value);
  return parsed || null;
};

const formatCustomFieldDate = (date) => {
  if (!date) return '';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const getUrlHref = (value) => {
  if (!value) return '';
  const trimmedValue = String(value).trim();
  return /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
};

const isUrlFieldType = (field) =>
  URL_CUSTOM_FIELD_TYPES.includes(field?.type) ||
  URL_CUSTOM_FIELD_TYPE_TITLES.includes(field?.typeTitle);

const getInputType = (fieldType) => {
  if (fieldType.type === 'phone') return 'tel';
  return 'text';
};

export const getCustomFieldUrlError = (value, field) => {
  if (!isUrlFieldType(field)) return '';

  const trimmedValue = String(value || '').trim();
  if (!trimmedValue) return '';

  if (!validURL(trimmedValue)) {
    return MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_URL_VALIDATION;
  }

  return '';
};

export const getCustomFieldUrlErrors = (customFields, allCustomFields) =>
  (customFields || []).reduce((result, field) => {
    const fieldName = getCustomFieldName(field);
    const fieldValue = getCustomFieldValue(field);
    const fieldType = getFieldType(fieldName, allCustomFields);
    const error = getCustomFieldUrlError(fieldValue, fieldType);

    if (fieldName && error) {
      result[fieldName] = error;
    }

    return result;
  }, {});

const CustomFields = (props) => {
  const { isEditing, customFields, setCustomFields } = props;

  const allCustomFields = useSelector((state) => state?.customFieldsReducers?.allCustomFields);

  const handleUpdateCustomFields = (label, value) => {
    const updatedCustomFields = customFields.map((field) =>
      getCustomFieldName(field) === label ? { [label]: value } : field
    );
    setCustomFields(updatedCustomFields);
  };

  if (!allCustomFields || allCustomFields.length === 0) {
    return (
      <Box className="emptyTabContent customFieldsEmptyTabContent">
        <img src={NoCustomFieldsCIcon} alt="Custom Fields" width="40" height="40" />
        <Typography variant="h6">
          {MESSAGES.CONTACTS.CUSTOM_FIELDS_TAB.CUSTOM_FIELDS_TAB_TITLE}
        </Typography>
        <Typography>{MESSAGES.CONTACTS.CUSTOM_FIELDS_TAB.CUSTOM_FIELDS_TAB_DESCRIPTION}</Typography>
        <Link to="/settings/custom-fields">
          {MESSAGES.CONTACTS.CUSTOM_FIELDS_TAB.ENABLE_CUSTOM_FIELDS}
        </Link>
      </Box>
    );
  }

  return (
    <>
      {!isEditing ? (
        <Box className="detailsGridLayout">
          <Box className="detailsRow">
            {customFields?.map((item, colIndex) => {
              const details = item;
              if (!details) return null;
              const fieldName = getCustomFieldName(details);
              const fieldValue = getCustomFieldValue(details);
              const fieldType = getFieldType(fieldName, allCustomFields);
              return (
                <Box key={colIndex} className="detailsColumn">
                  <Typography className="detailsAttribute">{fieldName}</Typography>
                  {isUrlFieldType(fieldType) && fieldValue ? (
                    <Typography
                      component="a"
                      href={getUrlHref(fieldValue)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="detailsValue customFieldUrlValue"
                    >
                      {fieldValue}
                    </Typography>
                  ) : (
                    <Typography className="detailsValue">
                      {fieldValue || ' -'}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      ) : (
        <Box className="editFormLayoutCustomFields">
          <Box className="editFormRowCustomFields">
            {customFields?.map((field, index) => {
              const fieldName = getCustomFieldName(field);
              const fieldValue = getCustomFieldValue(field) || '';
              const fieldType = getFieldType(fieldName, allCustomFields);
              const inputType = getInputType(fieldType);
              const errorMessage = getCustomFieldUrlError(fieldValue, fieldType);

              return (
                <Box key={index} className="editFormColumnCustomFields">
                  <Typography className="editFieldLabelCustomFields">{fieldName}</Typography>
                  {fieldType.type === 'date' ? (
                    <Box className="mailedDate newDatePicker updatedDesign customFieldDatePicker">
                      <Box className="selectDate">
                        <DatePicker
                          selected={parseCustomFieldDate(fieldValue)}
                          onChange={(date) => {
                            if (date) {
                              dateProtector(date);
                            }
                            handleUpdateCustomFields(fieldName, formatCustomFieldDate(date));
                          }}
                          onChangeRaw={(e) => {
                            const { value } = e.target;
                            if (value === '') {
                              handleUpdateCustomFields(fieldName, '');
                              return;
                            }
                            if (isValidDate(value)) {
                              handleUpdateCustomFields(fieldName, value);
                            } else {
                              e.preventDefault();
                            }
                          }}
                          onBlur={(e) => {
                            if (e.target.value === '') {
                              handleUpdateCustomFields(fieldName, '');
                            }
                          }}
                          dateFormat="MM/dd/yyyy"
                          className="datePicker editTextFieldCustomFields"
                          calendarClassName="customFieldDatePickerCalendar"
                          popperClassName="customFieldDatePickerPopper"
                          popperPlacement="bottom-start"
                          popperModifiers={[
                            {
                              name: 'offset',
                              options: {
                                offset: [0, 6],
                              },
                            },
                            {
                              name: 'preventOverflow',
                              options: {
                                rootBoundary: 'viewport',
                                padding: 16,
                              },
                            },
                            {
                              name: 'flip',
                              options: {
                                fallbackPlacements: ['top-start', 'bottom-start'],
                              },
                            },
                          ]}
                          popperProps={{
                            strategy: 'fixed',
                          }}
                          placeholderText={getFieldPlaceholder(fieldType.type)}
                          isClearable
                        />
                      </Box>
                    </Box>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        name={fieldName || 'name'}
                        type={inputType}
                        value={fieldValue}
                        onChange={(e) => handleUpdateCustomFields(fieldName, e.target.value)}
                        variant="outlined"
                        className="editTextFieldCustomFields"
                        placeholder={getFieldPlaceholder(fieldType.type)}
                        error={Boolean(errorMessage)}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                              {fieldValue && (
                                <IconButton
                                  className="cpClearInputBtn"
                                  size="small"
                                  onClick={() => handleUpdateCustomFields(fieldName, '')}
                                  edge="end"
                                  sx={{ right: '15px' }}
                                >
                                  <CloseIcon fontSize="small" />
                                </IconButton>
                              )}
                            </InputAdornment>
                          ),
                        }}
                      />
                      {errorMessage ? (
                        <Typography className="customFieldErrorMessage">
                          <sup>*</sup>
                          {errorMessage}
                        </Typography>
                      ) : null}
                    </>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </>
  );
};

export default CustomFields;
